<template>

    <kt-modal 
        :visible="recordVisible" 
        :title="recordTitle"
        :size="'modal-xl'" 
        @dismiss="onDismiss()"
    >
        <template #body>
            <pm-form
                v-if="isPM"
                v-model="pmRecord"
            ></pm-form>
            <job-form
                v-else 
                ref="jobFormRef"
                v-model="record"
            ></job-form>
        </template>

        <template #footer>
            <div class="kt-modal-actions d-flex align-items-center justify-content-between w-100">
                <div class="form-check form-switch">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="sendNotificationToggle"
                        v-model="sendNotification"
                    >
                    <label class="form-check-label" for="sendNotificationToggle">
                        Send Update Notification
                    </label>
                </div>
                <div>
                    <button @click="onDismiss()" class="btn btn-outline-secondary">
                        <X :size="16" class="me-1" />Close
                    </button>
                    <button v-if="record.Id || pmRecord.Id" @click="deleteRecord()" class="btn btn-outline-danger ms-2">
                        <Trash2 :size="16" class="me-1" />Delete
                    </button>
                    <button @click="saveRecord()" class="btn btn-primary ms-2">
                        <Save :size="16" class="me-1" />Save
                    </button>
                </div>
            </div>
        </template>
    </kt-modal>

</template>

<script lang="ts" setup>

import { ref, watch, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import Api from "@/services/Api";

import Emitter from "@/services/Emitter";
import ApiService from "@/services/ApiService";
import { JobModel, formatJobTitle } from "@/models/RequestModel";
import { PMModel } from "@/models/PMModel";

import Alert from "@/services/Alert";
import Helper from "@/services/Helper";
import JobForm from "@/components/Jobs/JobForm.vue";
import PmForm from "@/components/PMs/PMForm.vue";
import KtModal from "@/components/UI/KtModal.vue";
import { X, Trash2, Save } from 'lucide-vue-next';

const route = useRoute();
const router = useRouter();
const recordTitle = ref("Job Entry");
const recordVisible = ref(true);
const isPM = ref(false);
const pmRecord = ref<PMModel>(new PMModel());
const unwatch:Array<Function> = [];

const props = defineProps<{
    id?: number | string | null;
}>();

const emit = defineEmits(['close']);

const newRecord = ():JobModel => 
{
    return new JobModel();

};

const record = ref<JobModel>(newRecord());
const jobFormRef = ref<InstanceType<typeof JobForm> | null>(null);
const sendNotification = ref(false);

const saveRecord = async () => {
    if (!isPM.value && !record.value.Technician)
    {
        Alert.notify({
            text: "Please select a technician."
        });
        return;
    }

    try {
        let data;
        
        if (isPM.value) {
            // Save as PM
            data = await ApiService.savePM(pmRecord.value);
        } else {
            // Save as regular request
            console.debug("save", record.value);
            
            // Get pending roles before save (for new jobs)
            const pendingRoleIds = jobFormRef.value?.getPendingRoleIds?.() || [];
            const isNewJob = !record.value.Id;
            
            data = await ApiService.saveRequest(record.value);
            
            // Apply pending roles after saving new job
            if (isNewJob && pendingRoleIds.length > 0 && data.Id) {
                try {
                    await ApiService.updateRequestRoles(data.Id, pendingRoleIds, true);
                    jobFormRef.value?.clearPendingRoles?.();
                } catch (error) {
                    console.error('Failed to apply roles to new job:', error);
                }
            }
        }
        
        // Check if we're in a route-based context or global modal
        if (route.name && route.name.toString().includes('Job')) {
            if (isPM.value) {
                Emitter.emit("redirect", "/pm/edit/" + data.Id);
            } else {
                Emitter.emit("redirect", "/job/edit/" + data.Id);
            }
        } else {
            // Global modal - close after save
            Alert.notify({
                text: isPM.value ? "PM saved successfully!" : "Request saved successfully!",
                type: "success"
            });
            emit('close');
        }
    } catch (error) {
        console.error("Save failed:", error);
    }
};

const deleteRecord = async () => {
    Alert.notify({
        text: "Deletion is currently disabled"
    });
};


const onDismiss = () => {
    // Check if we're in a route-based context (has ID in route) or global modal
    if (route.params.id && route.name && route.name.toString().includes('Job')) {
        // Use router.back() to preserve filters/query params from previous page
        router.back();
    } else {
        // Global modal - emit close event
        emit('close');
    }
};

const loadRecords = async (val:string) =>
{
    switch (val) {
        case "new":
            record.value = newRecord();
            recordTitle.value = "Job Entry";
            isPM.value = false;
            break;

        default:
            let rec = await ApiService.loadRequest(val + "");
            console.debug("Loaded job:", rec);
            
            // Check if this Request is linked to a PM - if so, show PM form instead
            if (rec.Id) {
                try {
                    const linkedPM = await ApiService.loadPMByRequest(rec.Id);
                    if (linkedPM && linkedPM.Id) {
                        console.log("Request is linked to PM, showing PM form:", linkedPM.Id);
                        // Show PM form instead of job form
                        isPM.value = true;
                        pmRecord.value = linkedPM;
                        recordTitle.value = "PM Entry";
                        return;
                    }
                } catch (error) {
                    console.debug("No PM linked to this request, proceeding normally");
                }
            }
            
            // Regular request
            isPM.value = false;
            record.value = rec;

            // Only show ID info if we have an actual record
            if (rec && rec.Id) {
                recordTitle.value = formatJobTitle(rec as any) || "Job Entry";
            } else {
                recordTitle.value = "Job Entry";
            }



            break;
    }
};


// Compute the effective ID - prop takes precedence over route param
const getEffectiveId = (): string => {
    if (props.id !== undefined && props.id !== null) {
        return props.id.toString();
    }
    if (route.params.id) {
        return route.params.id.toString();
    }
    return 'new';
};

// Watch for prop changes
unwatch.push(watch(() => props.id, (nv, ov) => {
    if (nv !== undefined && nv !== null) {
        loadRecords(nv.toString());
    }
}));

// Watch for route param changes (only if no prop is provided)
unwatch.push(watch(() => route.params.id, (nv, ov) => {
    if (props.id === undefined || props.id === null) {
        loadRecords(nv ? nv.toString() : 'new');
    }
}, { immediate: true }));

// Initial load if prop is provided (since route watcher won't trigger)
if (props.id !== undefined && props.id !== null) {
    loadRecords(props.id.toString());
}

onUnmounted(() =>
{
    unwatch.forEach((rw) => {
        rw();
    });
})

</script>